import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button, cn } from '@frontend/ui'

interface StatsTablePaginationProps {
  page: number
  pageCount: number
  onPageChange: (page: number) => void
}

// Builds a compact page list like [1, '…', 4, 5, 6, '…', 10] for the current
// page. Keeps the control small while preserving jump-to-end behaviour.
function buildPageList(current: number, total: number): (number | '…')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i)
  const out: (number | '…')[] = []
  out.push(0)
  if (current > 2) out.push('…')
  const start = Math.max(1, current - 1)
  const end = Math.min(total - 2, current + 1)
  for (let i = start; i <= end; i++) out.push(i)
  if (current < total - 3) out.push('…')
  out.push(total - 1)
  return out
}

export function StatsTablePagination({
  page,
  pageCount,
  onPageChange
}: StatsTablePaginationProps) {
  if (pageCount <= 1) return null
  const pages = buildPageList(page, pageCount)

  return (
    <div className='flex items-center justify-end gap-1 text-sm'>
      <Button
        variant='ghost'
        size='sm'
        disabled={page === 0}
        onClick={() => onPageChange(page - 1)}
        className='gap-1'
      >
        <ChevronLeft className='h-4 w-4' />
        Previous
      </Button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span
            key={`ellipsis-${i}`}
            className='text-muted-foreground px-2 select-none'
          >
            …
          </span>
        ) : (
          <Button
            key={p}
            variant={p === page ? 'outline' : 'ghost'}
            size='sm'
            onClick={() => onPageChange(p)}
            className={cn(
              'min-w-9',
              p === page && 'text-foreground font-semibold'
            )}
          >
            {p + 1}
          </Button>
        )
      )}
      <Button
        variant='ghost'
        size='sm'
        disabled={page === pageCount - 1}
        onClick={() => onPageChange(page + 1)}
        className='gap-1'
      >
        Next
        <ChevronRight className='h-4 w-4' />
      </Button>
    </div>
  )
}
